import { ParcelDto } from '@shared/types/parcel';
import {
  Block,
  CloseIcon,
  Column,
  DateCellComponent,
  DefaultTableFilterProps,
  DetailsIcon,
  Loader,
  SuccessLineIcon,
  useNotifications,
} from '@uni-space/uni-components';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getParcelPhotos } from '../api';
import { ParcelPhotoDto } from '../model/parcel';
import ParcelPhotosModal from '../ui/ParcelPhotosModal';

export const useParcelSettingsTable = () => {
  const [loadingTrackNumber, setLoadingTrackNumber] = useState<string>('');
  const [photosTrackNumber, setPhotosTrackNumber] = useState<string>('');
  const [photos, setPhotos] = useState<ParcelPhotoDto[]>([]);
  const { addNotification } = useNotifications();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const { t } = useTranslation();

  const filters: DefaultTableFilterProps<ParcelDto>[] = useMemo(
    () => [
      {
        type: 'date',
        id: 'scanAt',
        placeholder: t('tableHeader.scanAt'),
      },
      {
        type: 'search',
        id: 'trackNumbers',
        name: 'Track Number',
        label: t('tableHeader.trackNumber'),
        variant: 'primary',
      },
    ],
    [t],
  );

  // Фото хранятся в Media, шлюз отдаёт список с presigned-ссылками: их может быть несколько
  // (приёмка и отмена), поэтому в модалке галерея, а не одна картинка.
  const handleGetPhotos = useCallback(
    async (trackNumber: string) => {
      try {
        setLoadingTrackNumber(trackNumber);
        const response = await getParcelPhotos(trackNumber);
        setPhotos(response.photos);
        setPhotosTrackNumber(trackNumber);
        setIsOpenModal(true);
      } catch (error) {
        addNotification({ type: 'error', message: t('cannotGetPhoto') });
      } finally {
        setLoadingTrackNumber('');
      }
    },
    [addNotification, t],
  );

  const ModalComponent = useCallback(() => {
    return (
      <ParcelPhotosModal
        isOpen={isOpenModal}
        trackNumber={photosTrackNumber}
        photos={photos}
        onClose={() => {
          setIsOpenModal(false);
          setPhotos([]);
        }}
      />
    );
  }, [isOpenModal, photos, photosTrackNumber]);

  const columns: Column<ParcelDto>[] = useMemo(
    () => [
      {
        accessor: 'trackNumber',
        header: t('tableHeader.photo'),
        textAlign: 'center',
        flex: 0.5,
        renderCell: (row) => {
          const { trackNumber } = row;
          return (
            <Block
              onClick={() => {
                handleGetPhotos(trackNumber);
              }}
              sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
            >
              {trackNumber === loadingTrackNumber ? <Loader size="s" /> : <DetailsIcon size="s" />}
            </Block>
          );
        },
      },
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        textAlign: 'center',
      },
      {
        header: t('tableHeader.regionZone'),
        accessor: 'regionZone',
        textAlign: 'center',
        flex: 0.5,
      },
      {
        header: t('bagCode'),
        accessor: 'bagCode',
        textAlign: 'center',
      },
      {
        header: t('sortingCode'),
        accessor: 'sortingCode',
        textAlign: 'center',
      },
      {
        header: t('isQualityControl'),
        accessor: 'isQualityControl',
        textAlign: 'center',
        flex: 0.5,
        renderCell: (r) =>
          r.isQualityControl ? <SuccessLineIcon size="xs" color="green" /> : <CloseIcon color="red" size="xs" />,
      },
      {
        header: t('isPostal'),
        accessor: 'isPostal',
        textAlign: 'center',
        flex: 0.5,
        renderCell: (r) => (r.isPostal ? <SuccessLineIcon size="xs" color="green" /> : <CloseIcon color="red" size="xs" />),
      },

      {
        header: t('tableHeader.service'),
        accessor: 'service',
        textAlign: 'center',
        renderCell: (row) => row.service.name,
      },

      {
        header: t('tableHeader.weight'),
        accessor: 'weight',
        textAlign: 'center',
        renderCell: (row) => row.weight.value + ' g',
      },
      {
        header: t('tableHeader.actualWeightHeader'),
        accessor: 'actualWeight',
        textAlign: 'center',
        renderCell: (row) => (row?.actualWeight?.value ? row.actualWeight.value + ' g' : '-'),
      },
      {
        header: t('tableHeader.scanAt'),
        accessor: 'scanAt',
        textAlign: 'center',
        renderCell: (row) => <DateCellComponent justify="center" date={row.scanAt} zone="local" />,
      },
      {
        header: t('tableHeader.addedToBagAt'),
        accessor: 'addedToBagAt',
        textAlign: 'center',
        renderCell: (row) => <DateCellComponent justify="center" date={row.addedToBagAt} zone="local" />,
      },
    ],
    [loadingTrackNumber, handleGetPhotos, t],
  );

  return { filters, columns, ModalComponent };
};
